
import LpButton from "@/app/ui/components/lp-button/index.vue";
import { MainAppController } from "@/app/ui/controllers/MainAppController";
import { ManageAreaController } from "@/app/ui/controllers/ManageAreaController";
import {
  AvailableGeoAddressApiRequest,
  CreateAreaApiRequest,
  ListAvailableCourierApiRequest,
  ListGeoAddressApiRequest
} from "@/data/payload/api/ManageAreaApiRequest";
import {
  CreateAreaPayload,
  GeoAddress,
  GeoAddressArea,
  ShiftRow
} from "@/domain/entities/ManageArea";
import { v4 as generateUUID } from "uuid";
import { defineComponent, onMounted, ref, watch, computed } from "vue";
import AreaNameInput from "./area-name-input/index.vue";
import AssignCourier from "./assign-courier/index.vue";
import DynamicShift from "./dynamic-shift/index.vue";
import GeoAddressTreeSelector from "./geo-address-tree-selector/index.vue";
import { PaginationV2 } from "@/domain/entities/Pagination";
import { ModalMessageEntities } from "@/domain/entities/MainApp";

export default defineComponent({
  name: "ManageAreaHeader",
  emits: ["forms-validation", "update:isValid", "update:form"],
  components: {
    AreaNameInput,
    GeoAddressTreeSelector,
    AssignCourier,
    DynamicShift,
    LpButton
  },
  setup(_, { emit }) {
    const areaName = ref<string>("");
    const existingAreaNames = ref<string[]>([]);
    const isAreaNameValid = ref<boolean>(false);

    const isShiftEnabled = ref<boolean>(false);
    const selectedCourier = ref<any>("");
    const availableGeoAddress = ref<GeoAddressArea[]>([]);
    const geoAddressValues = ref<number[]>([]);
    const geoAddress = ref<GeoAddress[]>([]);
    const shifts = ref<ListAvailableCourierApiRequest>(
      new ListAvailableCourierApiRequest()
    );
    const totalAvailableVillages = ref<number>(0);
    const areaForms = ref<any[]>([]);

    const createNewForm = () => ({
      formId: generateUUID(),
      areaName: "",
      selectedVillages: [],
      selectedCourier: "",
      isShiftMode: false,
      shifts: [],
      isValid: false
    });

    const uniqueVillages = computed<number[]>(() => {
      return [
        ...new Set(
          areaForms.value.flatMap((form) => form.selectedVillages || [])
        )
      ];
    });

    const totalAssignedVillages = computed(() => {
      return uniqueVillages.value.length;
    });

    const validateForm = (form: any): boolean => {
      // Validasi areaName
      if (!form.isValid || !form.areaName.trim()) {
        return false;
      }

      // Validasi selectedVillages
      if (!form.selectedVillages || form.selectedVillages.length === 0) {
        return false;
      }

      // Validasi selectedCourier
      if (!form.isShiftMode && !form.selectedCourier) {
        return false;
      }

      // Validasi shifts jika isShiftMode true
      if (form.isShiftMode) {
        if (!form.shifts || form.shifts.length === 0) {
          return false;
        }

        return form.shifts.every(
          (shift: any) => shift.courierId && shift.startTime && shift.endTime
        );
      }

      return true;
    };

    const validateAllForms = (): boolean => {
      if (areaForms.value.length === 0) {
        return false;
      }

      const allFormsValid = areaForms.value.every((form) => validateForm(form));
      return allFormsValid;
    };

    const emitFormsValidation = () => {
      const isValid = validateAllForms();
      const formData = new CreateAreaApiRequest({
        areas: areaForms.value.map((form) => {
          const shifts =
            form.shifts.length && form.isShiftMode
              ? (form.shifts as ShiftRow[])
              : [
                  new ShiftRow({
                    courierId: form.selectedCourier.id,
                    startTime: "00:00",
                    endTime: "23:59"
                  })
                ];

          return new CreateAreaPayload({
            areaName: form.areaName,
            geoAddressIds: form.selectedVillages,
            shifts: shifts.map((shift) => {
              return new ShiftRow({
                ...shift,
                startTime: shift.startTime || "00:00",
                endTime: shift.endTime || "23:59"
              });
            })
          });
        })
      });

      emit("update:isValid", isValid);
      emit("update:form", formData);

      return {
        isValid,
        forms: formData
      };
    };

    const removeAreaContainer = (formId: string) => {
      if (areaForms.value.length === 1) return;

      areaForms.value = areaForms.value.filter(
        (form) => form.formId !== formId
      );

      const validationResult = emitFormsValidation();
      emit("forms-validation", validationResult);
    };

    const confirmationDeleteArea = (formId: string) => {
      MainAppController.showMessageModal(
        new ModalMessageEntities({
          customParentButton: "w-full",
          customWrapperButton: "w-full",
          customButton: "w-full",
          customWidth: "417px",
          title: "Yakin Hapus Area?",
          image: "badge-confirmation-general",
          message:
            "Hapus area akan menghapus semua data yang telah ditambahkan.",
          textCancel: "Batal",
          textSuccess: "Simpan",
          onSubmit: () => {
            MainAppController.closeMessageModal();
            removeAreaContainer(formId);
          },
          onClose: () => MainAppController.closeMessageModal()
        })
      );
    };

    const addFormAreaContainer = () => {
      areaForms.value.push(createNewForm());
    };

    const handleValidation = (isValid: boolean) => {
      isAreaNameValid.value = isValid;
    };

    const getSummaryGeoAddress = async () => {
      MainAppController.showLoading();
      try {
        const resp = await ManageAreaController.getSummaryGeoAddress();
        totalAvailableVillages.value = resp.data.unassigned;
      } finally {
        MainAppController.closeLoading();
      }
    };

    const fetchAvailableGeoAddress = async () => {
      availableGeoAddress.value = await ManageAreaController.getAvailableGeoAddress(
        new AvailableGeoAddressApiRequest()
      );
    };

    const fetchGeoAddress = async () => {
      geoAddress.value = (
        await ManageAreaController.getGeoAddress(
          new ListGeoAddressApiRequest({
            pagination: new PaginationV2({
              limit: 99999
            })
          })
        )
      ).data;
    };

    const getShipmentType = (geoAddressIds: number[] = []) => {
      const shipment = [
        ...new Set(
          geoAddress.value
            .filter((ga) => geoAddressIds.includes(ga.geoAddressId))
            .map((ga) => ga.dedicatedShipment.split(", "))
            .flatMap((s) => s)
        )
      ];

      if (["Semua"].every((s) => shipment.includes(s))) return "Semua";
      if (
        ["Aggregator", "Customer", "Marketplace"].every((s) =>
          shipment.includes(s)
        )
      )
        return "Semua";
      if (["Aggregator", "Marketplace"].every((s) => shipment.includes(s)))
        return "Marketplace & Aggregator";
      if (["Aggregator", "Customer"].every((s) => shipment.includes(s)))
        return "Customer & Aggregator";
      if (["Marketplace", "Customer"].every((s) => shipment.includes(s)))
        return "Marketplace & Customer";

      return shipment.join(" & ");
    };

    onMounted(async () => {
      await fetchGeoAddress();
      await fetchAvailableGeoAddress();
      await getSummaryGeoAddress();

      areaForms.value.push(createNewForm());
    });

    const isDisabledAddForm = computed(() => {
      return totalAvailableVillages.value === totalAssignedVillages.value;
    });

    const selectedGeoAddressIds = computed(() => {
      return areaForms.value
        .map((val) => {
          return val.selectedVillages;
        })
        .flatMap((ids) => ids);
    });

    watch(
      areaForms,
      (newVal) => {
        const validationResult = emitFormsValidation();
        emit("forms-validation", validationResult);
      },
      { deep: true }
    );

    return {
      areaName,
      existingAreaNames,
      handleValidation,
      addFormAreaContainer,
      totalAssignedVillages,
      totalAvailableVillages,
      isShiftEnabled,
      selectedCourier,
      availableGeoAddress,
      geoAddressValues,
      shifts,
      areaForms,
      validateForm,
      validateAllForms,
      emitFormsValidation,
      removeAreaContainer,
      getShipmentType,
      isDisabledAddForm,
      selectedGeoAddressIds,
      confirmationDeleteArea
    };
  }
});
